import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, Info, ChevronRight, Home, ThermometerSun, Zap, Euro } from "lucide-react";

/**
 * Comparaison simplifiée entre Parcours par geste et Rénovation d'ampleur
 * Avec montants maximum clairement affichés
 */
export default function RenoComparisonSection({ 
  workTypes = [], 
  incomeCategory,
  housingAge,
  onSelectParcours 
}) {
  const [showDetails, setShowDetails] = useState(false);

  const gestesCount = workTypes.length;
  const hasIsolation = workTypes.some(w => w.includes('isolation'));
  const hasChauffage = workTypes.some(w => w.includes('pompe_chaleur') || w.includes('chaudiere'));
  const canQualifyForAmpleur = gestesCount >= 2 && hasIsolation;

  // Plafonds MaPrimeRénov' par geste selon catégorie (2026)
  const MPR_CEILINGS = { bleu: 11000, jaune: 9000, violet: 5000, rose: 0 };
  const mprCeiling = MPR_CEILINGS[incomeCategory] || 5000;

  // Barèmes MaPrimeRénov' Rénovation d'Ampleur 2026
  // Source: france-renov.gouv.fr - PLF 2026 voté février 2026
  // Plafond dépenses HT: 30 000€ (gain 2 classes) ou 40 000€ (gain 3+ classes)
  // ATTENTION: Bonus sortie passoire SUPPRIMÉ en 2026
  const RENO_AMPLEUR = {
    bleu: { rate: 80, max_2classes: 24000, max_3classes: 32000 },   // 80% très modestes
    jaune: { rate: 60, max_2classes: 18000, max_3classes: 24000 },  // 60% modestes
    violet: { rate: 45, max_2classes: 13500, max_3classes: 18000 }, // 45% intermédiaires
    rose: { rate: 10, max_2classes: 3000, max_3classes: 4000 },     // 10% supérieurs
  };
  const renoAmpleur = RENO_AMPLEUR[incomeCategory] || RENO_AMPLEUR.violet;
  // Afficher le montant max pour gain 3+ classes (le plus avantageux)
  const renoAmpleurMax = renoAmpleur.max_3classes;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-4 text-white">
        <h3 className="font-bold text-base flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-400" />
          Choisissez votre parcours
        </h3>
        <p className="text-white/70 text-sm mt-1">
          {gestesCount} travaux • Revenus <span className="capitalize font-medium text-yellow-300">{incomeCategory}</span>
        </p>
      </div>

      <div className="p-4">
        <div className="grid md:grid-cols-2 gap-4">
          
          {/* Option 1: Mono-geste */}
          <div className={`relative border-2 rounded-xl p-4 transition-all ${
            !canQualifyForAmpleur ? 'border-[#1A4D2E] bg-[#E8F5E9]/20 shadow-md' : 'border-slate-200 hover:border-slate-300'
          }`}>
            {!canQualifyForAmpleur && (
              <div className="absolute -top-3 left-4 px-3 py-1 bg-[#1A4D2E] text-white text-xs font-bold rounded-full">
                RECOMMANDÉ
              </div>
            )}
            
            <div className="flex items-center gap-3 mb-4 mt-1">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Home className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-800">Par geste</h4>
                <p className="text-xs text-slate-500">MaPrimeRénov' + CEE</p>
              </div>
            </div>
            
            {/* Montant max */}
            <div className="bg-blue-50 rounded-lg p-4 mb-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Plafond MaPrimeRénov'</p>
              <p className="text-3xl font-bold text-[#1A4D2E]">{mprCeiling.toLocaleString('fr-FR')} €</p>
              <p className="text-xs text-slate-500 mt-1">+ Prime CEE (variable)</p>
            </div>

            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>Démarches simplifiées</span>
              </li>
              <li className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>Sans audit énergétique</span>
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <Info className="h-4 w-4 flex-shrink-0" />
                <span>Montants forfaitaires par travaux</span>
              </li>
            </ul>
          </div>

          {/* Option 2: Réno d'ampleur */}
          <div className={`relative border-2 rounded-xl p-4 transition-all ${
            canQualifyForAmpleur ? 'border-[#1A4D2E] bg-[#E8F5E9]/20 shadow-md' : 'border-slate-200 opacity-75'
          }`}>
            {canQualifyForAmpleur && (
              <div className="absolute -top-3 left-4 px-3 py-1 bg-[#1A4D2E] text-white text-xs font-bold rounded-full">
                ÉLIGIBLE
              </div>
            )}
            
            <div className="flex items-center gap-3 mb-4 mt-1">
              <div className="w-12 h-12 rounded-xl bg-[#1A4D2E] flex items-center justify-center">
                <ThermometerSun className="h-6 w-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-slate-800">Réno d'ampleur</h4>
                <p className="text-xs text-slate-500">Parcours accompagné</p>
              </div>
            </div>
            
            {/* Montant max */}
            <div className="bg-gradient-to-br from-[#E8F5E9] to-[#d4edda] rounded-lg p-4 mb-4 text-center">
              <p className="text-xs text-slate-600 mb-1">Jusqu'à {renoAmpleur.rate}% des travaux</p>
              <p className="text-3xl font-bold text-[#1A4D2E]">{renoAmpleurMax.toLocaleString('fr-FR')} €</p>
              <p className="text-xs text-slate-500 mt-1">
                Pour un gain de 3+ classes DPE (plafond 40k€ HT)
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Gain 2 classes : {renoAmpleur.max_2classes.toLocaleString('fr-FR')}€ max
              </p>
            </div>

            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>Accompagnement personnalisé</span>
              </li>
              <li className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>Plafond très élevé</span>
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <Info className="h-4 w-4 flex-shrink-0" />
                <span>Audit énergétique obligatoire</span>
              </li>
            </ul>

            {!canQualifyForAmpleur && (
              <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Conditions :</strong> 2 gestes d'isolation min + gain de 2 classes DPE
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Bouton voir plus */}
        <button 
          onClick={() => setShowDetails(!showDetails)}
          className="w-full mt-4 text-sm text-[#1A4D2E] hover:text-[#143d24] flex items-center justify-center gap-1 py-2"
        >
          {showDetails ? "Masquer les détails" : "Comparer en détail"}
          <ChevronRight className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
        </button>

        {/* Détails */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid md:grid-cols-2 gap-4 text-xs">
            <div className="bg-slate-50 rounded-lg p-3">
              <h5 className="font-semibold text-slate-700 mb-2">📋 Parcours par geste</h5>
              <ul className="space-y-1 text-slate-600">
                <li>• Logement de plus de 15 ans</li>
                <li>• Résidence principale</li>
                <li>• Artisan RGE obligatoire</li>
                <li>• Forfaits : isolation 25-75€/m², PAC 5000€</li>
                <li>• Prime CEE cumulable</li>
                <li>• Délai : 2-4 semaines</li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <h5 className="font-semibold text-slate-700 mb-2">🏠 Réno d'ampleur 2026</h5>
              <ul className="space-y-1 text-slate-600">
                <li>• Minimum 2 gestes d'isolation</li>
                <li>• Gain ≥2 classes DPE obligatoire</li>
                <li>• Audit énergétique obligatoire</li>
                <li>• Accompagnateur Rénov' + France Rénov' obligatoires</li>
                <li>• Plafond travaux : 70 000€ HT</li>
                <li>• Délai : 2-4 mois</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
